import React from 'react'
import { Badge, Menu, MenuProps } from 'antd'
import {
  CustomerServiceOutlined,
  FolderOpenOutlined,
  ReadOutlined,
  StockOutlined
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { chatSelectors } from 'store/chat'
import { adminRoutes } from 'shared/constants/routes'
import { getWindowWidth } from 'shared/utils/getWindowWidth'
import { useIsTouchLandscape } from 'shared/hooks/useIsTouchLandscape'
import { useSellerAccess } from 'shared/hooks/useSellerAccess'
import { MobileMenu } from '../MobileMenu/MobileMenu'

const SUPPORT_KEY = 'support-chat'
const CHATS_PATH = `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_CHATS}`

const items: MenuProps['items'] = [
  {
    label: 'Мои товары',
    key: adminRoutes.GOODS,
    icon: <FolderOpenOutlined />
  },
  {
    label: 'Мои заказы',
    key: adminRoutes.ORDERS,
    icon: <StockOutlined />
  },
  {
    label: 'Профиль',
    key: adminRoutes.PROFILE,
    icon: <ReadOutlined />
  },
  {
    label: 'Поддержка',
    key: SUPPORT_KEY,
    icon: <CustomerServiceOutlined />
  }
]

export const AdminMenu: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const unreadChatCount = useAppSelector(chatSelectors.selectUnreadCount)
  const isTouchLandscape = useIsTouchLandscape()
  const { canSeeSection } = useSellerAccess()

  const [current, setCurrent] = React.useState('')
  const [windowWidth, setWindowWidth] = React.useState<number>(getWindowWidth)
  const [isOpenMobileMenu, setIsOpenMobileMenu] = React.useState(false)

  React.useEffect(() => {
    setCurrent(`/${location.pathname.split('/')[1]}`)
  }, [location.pathname])

  React.useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(getWindowWidth())
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const menuItems = React.useMemo(() => items.filter(
    (item) => !item || !('key' in item) || canSeeSection(String(item.key))
  ).map((item) => {
    if (!item || !('key' in item) || !('label' in item) || item.key !== adminRoutes.PROFILE) return item

    return {
      ...item,
      label: (
        <Badge
          count={unreadChatCount}
          size='small'
          offset={[8, -2]}
        >
          <span>{item.label}</span>
        </Badge>
      )
    }
  }), [unreadChatCount, canSeeSection])

  const onClick: MenuProps['onClick'] = (e) => {
    setIsOpenMobileMenu(false)
    if (e.key === SUPPORT_KEY) {
      setCurrent(adminRoutes.PROFILE)
      return navigate(CHATS_PATH, { state: { openSupport: true } })
    }
    setCurrent(e.key)
    navigate(e.key)
  }

  return (
    <>
      {/* без `windowWidth &&`: при 0 React рендерил бы сам ноль вместо меню */}
      {windowWidth >= 768 && !isTouchLandscape ? (
        <Menu
          onClick={onClick}
          selectedKeys={[current]}
          mode='horizontal'
          items={menuItems}
          disabledOverflow
        />
      ) : (
        <MobileMenu
          isOpen={isOpenMobileMenu}
          setIsOpen={setIsOpenMobileMenu}
          Menu={
            <Menu
              onClick={onClick}
              selectedKeys={[current]}
              mode='vertical'
              items={menuItems}
              style={{ borderInlineEnd: 'none' }}
            />
          }
        />
      )}
    </>
  )
}
